import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import { format, getTimezoneData } from '@chilipiper/date-time'
import { Box, Grid, Icon, Text, styled } from '@chilipiper/design-system'
import { Popover, TimezoneList, UnstyledButton } from '@chilipiper/design-system/src/new'
import { SvgDropdown } from '@chilipiper/icons/src/design-system'
import * as R from 'remeda'
import { DEFAULT_LOCALE, T, useLocale } from '@chilipiper/locale/src/new'
import { useTranslations } from '../../../context/TranslationContext'
import { getCurrentTime } from './BookingMeeting'
import { customerColorCss } from './styles'

export type TimesEvents = { scrollIntoView(): void }

type Props = {
  durationInMinutes: number
  eventsRef?: MutableRefObject<TimesEvents | null | undefined>
  onTimeChange: (time: string) => void
  onTimezoneChange: (timezone: string) => void
  startTimes?: Array<string>
  time?: string
  timezone: string
}

const getSlotLabel = (startTime: Date | number, locale: string) => {
  const enUsLocale = format(startTime, 'p', { locale }).split(' ')
  const otherLocales = format(startTime, 'HH:mm', { locale }).split(' ')
  const [hour, period] = locale === DEFAULT_LOCALE ? enUsLocale : otherLocales
  return {
    hour,
    period: period ?? '',
  }
}

export const Times = ({
  onTimeChange,
  startTimes,
  time,
  timezone,
  onTimezoneChange,
  durationInMinutes,
  eventsRef,
}: Props) => {
  const [timezoneSearch, setTimezoneSearch] = useState('')
  const [isTimezonePickerOpen, setIsTimezonePickerOpen] = useState<boolean>(false)
  const timeSlotsRef = useRef<HTMLDivElement>(null)
  const locale = useLocale()
  const [timeBookHeader, meetingLength] = useTranslations(['timeBookHeader', 'meetingLength'])
  useEffect(() => {
    if (eventsRef) {
      eventsRef.current = {
        scrollIntoView: () => {
          setTimeout(
            () => timeSlotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
            0
          )
        },
      }
      return () => (eventsRef.current = null)
    }
    return R.doNothing()
  }, [eventsRef])

  return (
    <Box p={5}>
      <Text display='block' mb={3} textStyle='heading-s' color='text/body-major'>
        {timeBookHeader}
      </Text>
      <Text display='block' mb={2} textStyle='body-monoline' color='text/body-moderate'>
        <T data={{ duration: durationInMinutes.toString() }}>
          {meetingLength ?? '{duration} minute meeting'}
        </T>
      </Text>
      <Popover
        onOpenChange={setIsTimezonePickerOpen}
        isOpen={isTimezonePickerOpen}
        trigger={triggerProps => (
          <UnstyledButton display='flex' gap={1} ariaProps={triggerProps}>
            <Text display='block' textStyle='body-monoline' color='text/body-moderate'>
              {getTimezoneData(timezone)?.name} ({getCurrentTime(new Date(), locale, timezone)})
            </Text>
            <Icon icon={SvgDropdown} color='icon/indicator' />
          </UnstyledButton>
        )}
      >
        <TimezoneList
          size='S'
          hasTime
          aria-label='Preferred timezone'
          onSearchChange={setTimezoneSearch}
          value={timezone}
          search={timezoneSearch}
          onChange={newTimezone => {
            if (newTimezone) {
              onTimezoneChange(newTimezone)
            }
            setIsTimezonePickerOpen(false)
          }}
        />
      </Popover>
      <Grid rowGap={3} columnGap={2} gridTemplateColumns='1fr 1fr' pt={5} ref={timeSlotsRef}>
        {startTimes?.map(startTime => {
          const { hour, period } = getSlotLabel(new Date(startTime), locale)

          return (
            <TimeButton
              key={startTime}
              bg='bg/button-moderate'
              color='text/action-moderate'
              borderRadius={10}
              py='13px'
              textAlign='center'
              fontSize={14}
              fontWeight={600}
              onClick={() => onTimeChange(startTime)}
              isSelected={startTime === time}
            >
              {`${hour} ${period}`}
            </TimeButton>
          )
        })}
      </Grid>
    </Box>
  )
}

const TimeButton = styled(UnstyledButton)<{ isSelected: boolean }>`
  transition:
    background-color 0.2s,
    color 0.2s;

  ${({ isSelected }) => (isSelected ? customerColorCss : undefined)}
  :hover,
    :focus {
    ${customerColorCss}
  }
`
